/**
 * Vehicle-aware search results.
 *
 * Shopify ranks title matches above tags, so "wiper blades Focus 2015" put a Kia
 * Sorento 2015-2019 above the Ford Focus 2011-2018 whose 2015 lives in a tag.
 * When the query names a car we know, the page is rebuilt from tag-filtered
 * collections instead: mirror glass first (split into clip-on and stick-on),
 * then wiper blades. Anything we cannot resolve keeps Shopify's own results.
 */
(function () {
  const script = document.currentScript || document.querySelector('script[data-vehicles]');
  const VEHICLES_URL = script && script.dataset.vehicles;
  const TYPES = [
    {
      handle: 'wing-mirror-glass',
      title: 'Wing Mirror Glass',
      words: /\b(mirror|mirrors|glass)\b/,
      // Every glass carries exactly one of these tags (set from the SKU at import).
      subgroups: [
        {
          tag: 'clip-on',
          title: 'Clip-on glass',
          note: 'Comes fixed to a new backing plate that clips onto the mirror motor. Pick this if your old backing plate is cracked or missing.',
          words: /\bclip[\s-]?on\b|\bclip\b/,
        },
        {
          tag: 'stick-on',
          title: 'Stick-on glass',
          note: 'The glass only. It sticks onto your existing backing plate with the adhesive pads supplied, so pick this when the plate is still in good shape.',
          words: /\bstick[\s-]?on\b|\bstick\b|\badhesive\b/,
        },
      ],
    },
    { handle: 'wiper-blades', title: 'Wiper Blades', words: /\b(wiper|wipers|blade|blades)\b/ },
  ];
  const STOP = new Set(['for', 'the', 'a', 'an', 'my', 'car', 'fits', 'fit', 'to', 'of', 'and', 'side', 'replacement', 'new', 'spare', 'spares']);

  const params = new URLSearchParams(window.location.search);
  if (window.location.pathname !== '/search' || !params.get('q') || params.get('l4s') === 'off' || !VEHICLES_URL) return;

  const query = params.get('q');
  const text = query.toLowerCase();
  const words = text.replace(/[^a-z0-9\s./-]/g, ' ').split(/\s+/).filter((w) => w && !STOP.has(w));
  // Every year must match, so "peugeot 2008 2015" is the Peugeot 2008 in 2015.
  const years = words.filter((w) => /^(19|20)\d{2}$/.test(w));

  const phrasesOf = (list) => {
    const out = [];
    for (let i = 0; i < list.length; i++) {
      if (list[i + 2]) out.push(list.slice(i, i + 3).join(' '));
      if (list[i + 1]) out.push(list.slice(i, i + 2).join(' '));
      out.push(list[i]);
    }
    return out.sort((a, b) => b.length - a.length);
  };

  const countOf = (doc) => {
    const m = doc.body.textContent.match(/(\d[\d,]*)\s+items?/);
    return m ? Number(m[1].replace(/,/g, '')) : 0;
  };

  const fetchGroup = async (url) => {
    try {
      const doc = new DOMParser().parseFromString(await fetch(url).then((r) => r.text()), 'text/html');
      const grid = doc.querySelector('results-list');
      const count = countOf(doc);
      return grid && count ? { grid, count, url } : null;
    } catch (e) {
      return null;
    }
  };

  const askedFirst = (list) => {
    const asked = list.find((item) => item.words.test(text));
    return asked ? [asked, ...list.filter((item) => item !== asked)] : list;
  };

  const el = (tag, className, html) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (html) node.innerHTML = html;
    return node;
  };

  fetch(VEHICLES_URL)
    .then((r) => r.json())
    .then(async (data) => {
      // "peugeot 2008 2015": 2008 is the model, filtered by its make+model tag, not the 2008 year tag.
      const at = words.findIndex((w, i) => i > 0 && years.includes(w) && data.yearModels?.[`${words[i - 1]} ${w}`]);
      let modelTag = at > 0 ? data.yearModels[`${words[at - 1]} ${words[at]}`] : null;
      if (modelTag) years.splice(years.indexOf(words[at]), 1);
      const rest = words.filter((w) => !years.includes(w));
      let makeTag = null;
      for (const phrase of phrasesOf(rest)) {
        const alias = data.aliases[phrase] || phrase;
        if (!modelTag && data.models[phrase]) modelTag = data.models[phrase];
        if (!makeTag && data.makes[alias]) makeTag = data.makes[alias];
      }
      const vehicleTag = modelTag || makeTag;
      if (!vehicleTag) return;

      const tagPath = [vehicleTag, ...years].join('+');

      const sections = await Promise.all(
        askedFirst(TYPES).map(async (type) => {
          const url = `/collections/${type.handle}/${tagPath}`;
          if (!type.subgroups) {
            const group = await fetchGroup(url);
            return group && { type, url, count: group.count, groups: [{ ...group }] };
          }
          const subs = await Promise.all(
            askedFirst(type.subgroups).map(async (sub) => {
              const group = await fetchGroup(`${url}+${sub.tag}`);
              return group && { ...group, sub };
            })
          );
          const groups = subs.filter(Boolean);
          const count = groups.reduce((sum, g) => sum + g.count, 0);
          return count ? { type, url, count, groups } : null;
        })
      );

      const found = sections.filter(Boolean);
      // Nothing fits the year: say so. Shopify's own results drop a word that matches nothing,
      // so "toyota yaris 2026" would list Yaris parts for every other year.
      if (!found.length && !years.length) return;

      const host = document.querySelector('results-list');
      if (!host) return;
      const container = host.closest('.shopify-section') || host;
      const wrapper = el('div', 'l4s-grouped-results');
      if (found.length) {
        const total = found.reduce((sum, s) => sum + s.count, 0);
        wrapper.appendChild(
          el('p', 'l4s-grouped-results__intro', `${total} product${total === 1 ? '' : 's'} for your search, grouped by part. <a href="/search?q=${encodeURIComponent(query)}&l4s=off">See all search results instead</a>`)
        );
      } else {
        const allYears = query.split(/\s+/).filter((w) => !years.includes(w)).join(' ');
        const note = el(
          'p',
          'l4s-grouped-results__intro',
          `<a href="/search?q=${encodeURIComponent(allYears)}">Show parts for all years</a> or <a href="/search?q=${encodeURIComponent(query)}&l4s=off">see all search results</a>.`
        );
        note.prepend(`No parts found for "${query}". `);
        wrapper.appendChild(note);
      }

      for (const section of found) {
        const block = el('section', 'l4s-grouped-results__group');
        block.appendChild(
          el('div', 'l4s-grouped-results__header', `<h2>${section.type.title} (${section.count})</h2><a href="${section.url}">View all ${section.type.title.toLowerCase()}</a>`)
        );
        for (const group of section.groups) {
          if (group.sub) {
            const sub = el('div', 'l4s-grouped-results__subgroup');
            sub.appendChild(
              el('div', 'l4s-grouped-results__subheader', `<h3>${group.sub.title} (${group.count})</h3><a href="${group.url}">View all ${group.sub.title.toLowerCase()}</a>`)
            );
            sub.appendChild(el('p', 'l4s-grouped-results__note', group.sub.note));
            sub.appendChild(group.grid);
            block.appendChild(sub);
          } else {
            block.appendChild(group.grid);
          }
        }
        wrapper.appendChild(block);
      }

      container.replaceWith(wrapper);
      document.title = `${query} | ${document.title.split('|').pop().trim()}`;
    })
    .catch(() => {});
})();
